import React from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import farmerAvatar from '../components/farmer-avatar.png';
import './HomePage.css';

interface HowStep {
  label: string;
  desc: string;
}

interface ModuleItem {
  name: string;
  question: string;
}

const HomePage: React.FC = () => {
  const { t } = useTranslation();
  const features = t('home.hero.features', { returnObjects: true }) as string[];
  const steps = t('home.howItWorks.steps', { returnObjects: true }) as HowStep[];
  const modules = t('home.modules.items', { returnObjects: true }) as ModuleItem[];

  return (
    <div className="home">
      <section className="home-hero" data-testid="home-hero">
        <h1 className="home-hero__title">{t('home.hero.title')}</h1>
        <div className="home-hero__content">
          <p>{t('home.hero.subPrimary')}</p>
        </div>
        <div className="home-hero__side">
          <aside className="home-hero__features">
            <div className="home-features">
              {features.map((feature) => (
                <div className="home-feature" key={feature}>
                  ✔ {feature}
                </div>
              ))}
            </div>
          </aside>
          <div className="home-hero__actions">
            <Link to="/login" className="hero-primary">
              {t('home.hero.cta')}
            </Link>
          </div>
        </div>
      </section>

      <figure className="home-screenshot" data-testid="home-screenshot">
        <img
          src="/product-liquiditaetsplanung.png"
          alt={t('home.screenshot.alt')}
          className="home-screenshot__img"
          width={2000}
          height={1097}
          loading="lazy"
        />
        <figcaption className="home-screenshot__caption">{t('home.screenshot.caption')}</figcaption>
      </figure>

      <section className="home-usp" data-testid="home-usp">
        <h2>{t('home.usp.headline')}</h2>
        <p>{t('home.usp.body1')}</p>
        <p>{t('home.usp.body2')}</p>
      </section>

      <section className="home-how" data-testid="home-how">
        <h2>{t('home.howItWorks.title')}</h2>
        <ol className="home-how__stack">
          {steps.map((step) => (
            <li className="home-how__step" key={step.label}>
              <span className="home-how__label">{step.label}</span>
              <span className="home-how__desc">{step.desc}</span>
            </li>
          ))}
        </ol>
      </section>

      <section className="home-scenario" data-testid="home-scenario">
        <img src={farmerAvatar} alt="" aria-hidden="true" className="home-scenario__avatar" />
        <div className="home-scenario__body">
          <h2>{t('home.scenario.title')}</h2>
          <p>{t('home.scenario.intro')}</p>
          <p>{t('home.scenario.body')}</p>
          <p className="home-footnote">
            <span className="footnote-dot" />
            {t('home.scenario.exampleNote')}
          </p>
        </div>
      </section>

      <section className="home-modules" data-testid="home-modules">
        <h2>{t('home.modules.title')}</h2>
        <p className="home-modules__lead">{t('home.modules.lead')}</p>
        <div className="home-grid">
          {modules.map((module) => (
            <div className="home-card" key={module.name}>
              <h3>{module.name}</h3>
              <p>{module.question}</p>
            </div>
          ))}
        </div>
        <p className="home-modules__rollup">{t('home.modules.handlungsempfehlungen')}</p>
      </section>

      <div className="home-closing">
        <section className="home-vision" data-testid="home-vision">
          <h2>{t('home.vision.title')}</h2>
          <p>{t('home.vision.body')}</p>
        </section>

        <section className="home-contact" data-testid="home-contact">
          <div className="home-contact__card">
            <img
              src="/nicole-osmenda.jpg"
              alt={t('home.contact.photoAlt')}
              className="home-contact__photo"
            />
            <div className="home-contact__details">
              <div className="home-contact__label">{t('home.contact.label')}</div>
              <div className="home-contact__name">{t('home.contact.name')}</div>
              <div className="home-contact__role">{t('home.contact.role')}</div>
              <a className="home-contact__link" href={`mailto:${t('home.contact.email')}`}>
                {t('home.contact.email')}
              </a>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default HomePage;
